import React, { useState, useEffect, useRef } from 'react';
import { Wind, Play, Pause, X, CheckCircle, Brain } from 'lucide-react';
import { Button, Card, Spinner, Alert } from '../design-system';
import groundingService from '../../services/groundingService';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import ThoughtsAndFacts from './ThoughtsAndFacts';
import TaskCompletionCelebration from '../tasks/TaskCompletionCelebration';
import analyticsService from '../../services/analyticsService';

/**
 * Grounding Quick Access Component
 * Displays on patient dashboard for easy access to grounding exercises
 */
const GroundingQuickAccess = ({ onClose, assignment: propAssignment, loading: propLoading, onViewCards }) => {
  const { user } = useAuth();
  const [assignment, setAssignment] = useState(propAssignment || null);
  const [loading, setLoading] = useState(propLoading !== undefined ? propLoading : true);
  const [showExercise, setShowExercise] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  // Track if we've already auto-started to prevent restarting on prop changes
  const hasAutoStartedRef = useRef(false);

  // Update assignment when prop changes
  useEffect(() => {
    if (propAssignment !== undefined) {
      setAssignment(propAssignment);
      // Auto-start exercise when assignment is first provided (only once)
      if (propAssignment && !hasAutoStartedRef.current) {
        setShowExercise(true);
        hasAutoStartedRef.current = true;
      }
    }
  }, [propAssignment]);

  // Update loading state when prop changes
  useEffect(() => {
    if (propLoading !== undefined) {
      setLoading(propLoading);
    }
  }, [propLoading]);

  // Only load if assignment wasn't provided as prop
  useEffect(() => {
    if (propAssignment === undefined) {
      loadAssignment();
    }
  }, [propAssignment]);

  const loadAssignment = async () => {
    try {
      setLoading(true);
      const response = await groundingService.getAssignment(user);
      setAssignment(response.assignment);
      // Auto-start exercise when assignment loads (only once)
      if (response.assignment && !hasAutoStartedRef.current) {
        setShowExercise(true);
        hasAutoStartedRef.current = true;
      }
    } catch (error) {
      console.error('Error loading grounding assignment:', error);
      toast.error('Failed to load grounding assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleExerciseComplete = () => {
    // Show celebration modal before closing exercise
    setShowCelebration(true);
  };

  const handleExerciseExit = () => {
    // Exit without celebration - just close the exercise
    setShowExercise(false);
    if (onClose) {
      onClose();
    }
  };

  const handleCelebrationClose = () => {
    setShowCelebration(false);
    setShowExercise(false);
    // Close the grounding view after celebration
    if (onClose) {
      setTimeout(() => {
        onClose();
      }, 1000); // Small delay to show completion message
    }
  };

  if (loading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center gap-3">
            <Spinner size="medium" />
            <span className="text-gray-600">Loading grounding technique...</span>
          </div>
        </div>
      </Card>
    );
  }

  if (!assignment) {
    return (
      <Card className="border-dashed border-2 border-gray-300">
        <div className="text-center py-6">
          <Wind className="h-8 w-8 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Grounding Technique</h3>
          <p className="text-gray-600 text-sm">
            Your therapist hasn't assigned a grounding technique yet.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <>
      {/* Only show exercise when celebration is not showing */}
      {showExercise && !showCelebration && (
        assignment.techniqueType === 'breathing' ? (
          <BreathingExercise 
            assignment={assignment}
            onComplete={handleExerciseComplete}
            onExit={handleExerciseExit}
          />
        ) : (
          <ThoughtsAndFacts 
            assignment={assignment}
            onComplete={handleExerciseComplete}
            onExit={handleExerciseExit}
          />
        )
      )}

      {/* Grounding Completion Celebration Modal */}
      <TaskCompletionCelebration 
        isOpen={showCelebration}
        onClose={handleCelebrationClose}
        message="You're taking care of yourself"
      />
    </>
  );
};

/**
 * Breathing Exercise Component
 * Animated breathing exercise with visual and audio cues
 */
const BreathingExercise = ({ assignment, onComplete, onExit }) => {
  const { user } = useAuth();
  const [currentPhase, setCurrentPhase] = useState('inhale');
  const [cycleCount, setCycleCount] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(assignment.techniqueConfig.inhaleDuration);
  const [isRunning, setIsRunning] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const [countdown, setCountdown] = useState(3);
  // Track session start time for duration calculation
  const sessionStartTimeRef = useRef(null);

  const phases = [
    { 
      name: 'inhale', 
      duration: assignment.techniqueConfig.inhaleDuration, 
      instruction: 'Breathe in slowly...', 
      color: 'blue' 
    },
    { 
      name: 'hold', 
      duration: assignment.techniqueConfig.holdDuration, 
      instruction: 'Hold your breath...', 
      color: 'green' 
    },
    { 
      name: 'exhale', 
      duration: assignment.techniqueConfig.exhaleDuration, 
      instruction: 'Breathe out gently...', 
      color: 'purple' 
    }
  ];

  // Auto-start the exercise when component mounts
  useEffect(() => {
    // Countdown before starting
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          handleStart();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(countdownInterval);
  }, []); // Empty dependency array - only run once on mount

  useEffect(() => {
    let interval;
    
    if (isRunning && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => prev - 1);
      }, 1000);
    } else if (isRunning && timeRemaining === 0) {
      // Timer reached 0, move to next phase
      const currentPhaseIndex = phases.findIndex(p => p.name === currentPhase);
      const nextPhaseIndex = (currentPhaseIndex + 1) % phases.length;
      
      console.log('Phase transition:', {
        currentPhase,
        currentPhaseIndex,
        nextPhaseIndex,
        cycleCount
      });
      
      if (nextPhaseIndex === 0) {
        // Completed one full cycle (inhale -> hold -> exhale)
        console.log('Cycle completed, checking if should end');
        const newCycleCount = cycleCount + 1;
        console.log('New cycle count would be:', newCycleCount, 'Target:', assignment.techniqueConfig.cycles);
        
        // Check if we've reached the target cycles BEFORE transitioning
        if (newCycleCount >= assignment.techniqueConfig.cycles) {
          // We've completed all required cycles, complete the exercise
          console.log('Target cycles reached, completing exercise');
          setCycleCount(newCycleCount);
          // Stop the timer and complete
          setIsRunning(false);
          handleComplete();
        } else {
          // Continue to next cycle
          console.log('Continuing to next cycle');
          setCycleCount(newCycleCount);
          
          // Move to next cycle's inhale phase
          const nextPhase = phases[0]; // Start with inhale
          setCurrentPhase(nextPhase.name);
          setTimeRemaining(nextPhase.duration);
        }
      } else {
        // Move to next phase in current cycle
        const nextPhase = phases[nextPhaseIndex];
        console.log('Moving to next phase:', nextPhase.name);
        setCurrentPhase(nextPhase.name);
        setTimeRemaining(nextPhase.duration);
      }
    }

    return () => clearInterval(interval);
  }, [isRunning, timeRemaining, currentPhase, cycleCount, assignment.techniqueConfig.cycles]);

  // Handle completion when cycle count reaches target (safety net - main check is in phase transition)
  useEffect(() => {
    console.log('Cycle count changed:', cycleCount, 'Target:', assignment.techniqueConfig.cycles);
    if (cycleCount >= assignment.techniqueConfig.cycles && isRunning) {
      console.log('Exercise completed! (safety net check)');
      handleComplete();
    }
  }, [cycleCount, assignment.techniqueConfig.cycles, isRunning]);

  const handleStart = async () => {
    try {
      const response = await groundingService.startSession(
        user,
        assignment.groundingId,
        {}
      );
      setSessionId(response.session.sessionId);
      setIsRunning(true);
      // Track session start time for duration calculation
      sessionStartTimeRef.current = Date.now();
      
      // Track grounding session started event
      try {
        analyticsService.track('grounding_session_started', {
          sessionId: response.session.sessionId,
          groundingId: assignment.groundingId,
          techniqueType: assignment.techniqueType,
          isBreathing: assignment.techniqueType === 'breathing',
          isThoughtsFacts: assignment.techniqueType === 'thoughts-facts'
        });
      } catch (analyticsError) {
        // Don't break session start if analytics fails
        console.error('Analytics tracking error:', analyticsError);
      }
    } catch (error) {
      console.error('Error starting session:', error);
      toast.error('Failed to start grounding session');
    }
  };

  const handleComplete = async () => {
    // Prevent multiple completion calls
    if (isCompleting || !sessionId) {
      return;
    }
    
    try {
      setIsCompleting(true);
      const startTime = sessionStartTimeRef.current || Date.now();
      const response = await groundingService.completeSession(user, sessionId, {
        cyclesCompleted: cycleCount,
        interruptions: 0
      });
      
      // Calculate session duration (use response duration if available, otherwise calculate)
      const duration = response.session?.duration 
        ? Math.round(response.session.duration / 1000) // Convert from ms to seconds
        : Math.round((Date.now() - startTime) / 1000); // Fallback: calculate from start time
      
      // Track grounding session completed event
      try {
        analyticsService.track('grounding_session_completed', {
          sessionId: sessionId,
          groundingId: assignment.groundingId,
          techniqueType: assignment.techniqueType,
          isBreathing: assignment.techniqueType === 'breathing',
          isThoughtsFacts: assignment.techniqueType === 'thoughts-facts',
          cyclesCompleted: cycleCount,
          duration: duration,
          interruptions: 0
        });
      } catch (analyticsError) {
        // Don't break session completion if analytics fails
        console.error('Analytics tracking error:', analyticsError);
      }
      
      toast.success('Grounding exercise completed!');
      onComplete();
    } catch (error) {
      console.error('Error completing session:', error);
      toast.error('Failed to complete session');
    } finally {
      setIsCompleting(false);
    }
  };

  const handlePause = () => {
    setIsRunning(false);
  };

  const handleResume = () => {
    setIsRunning(true);
  };

  const currentPhaseData = phases.find(p => p.name === currentPhase);
  const rawProgressPercentage = ((cycleCount * 3 + phases.indexOf(currentPhaseData)) / (assignment.techniqueConfig.cycles * 3)) * 100;
  const progressPercentage = Math.min(rawProgressPercentage, 100); // Cap at 100% to prevent overflow
  const displayCycleNumber = Math.min(cycleCount + 1, assignment.techniqueConfig.cycles); // Cap cycle display at target

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
        <div className="text-center">
          <h3 className="text-xl font-semibold mb-6">Breathing Exercise</h3>
          
          {/* Animated Circle */}
          <div className="relative mb-8">
            <div 
              className={`w-32 h-32 mx-auto rounded-full transition-all duration-1000 ease-in-out ${
                currentPhase === 'inhale' ? 'bg-blue-200 scale-110' :
                currentPhase === 'hold' ? 'bg-green-200 scale-110' :
                'bg-purple-200 scale-100'
              }`}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl font-bold text-gray-700">{timeRemaining}</span>
            </div>
          </div>
          
          {/* Instructions */}
          {countdown > 0 ? (
            <div className="mb-4">
              <p className="text-lg font-medium text-blue-600 mb-2">Starting in...</p>
              <div className="text-4xl font-bold text-blue-600">{countdown}</div>
            </div>
          ) : (
            <p className="text-lg font-medium mb-4">{currentPhaseData.instruction}</p>
          )}
          
          {/* Progress */}
          {countdown === 0 && (
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-2">
                Cycle {displayCycleNumber} of {assignment.techniqueConfig.cycles}
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          )}

          
          {/* Controls */}
          <div className="flex space-x-4">
            <Button variant="outline" onClick={onExit || onComplete}>
              <X className="h-4 w-4 mr-2" />
              Exit
            </Button>
            
            {isRunning && (
              <Button onClick={handlePause} className="bg-yellow-600 hover:bg-yellow-700">
                <Pause className="h-4 w-4 mr-2" />
                Pause
              </Button>
            )}
            
            {!isRunning && sessionId && (
              <Button onClick={handleResume} className="bg-green-600 hover:bg-green-700">
                <Play className="h-4 w-4 mr-2" />
                Resume
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroundingQuickAccess;
